import { z } from 'zod'

export const engineUpgradeTargetSchema = z.object({
  engineVersion: z.string(),
  description: z.string().nullable(),
  autoUpgrade: z.boolean(),
  isMajorVersionUpgrade: z.boolean(),
})
export type EngineUpgradeTarget = z.infer<typeof engineUpgradeTargetSchema>

export const pendingMaintenanceActionSchema = z.object({
  resourceIdentifier: z.string(),
  action: z.string(),
  description: z.string().nullable(),
  autoAppliedAfterDate: z.string().nullable(),
  forcedApplyDate: z.string().nullable(),
  currentApplyDate: z.string().nullable(),
  optInStatus: z.string().nullable(),
})
export type PendingMaintenanceAction = z.infer<typeof pendingMaintenanceActionSchema>

/**
 * RDS's own `OptInType` wire values, not friendlier local names — they go
 * straight into ApplyPendingMaintenanceAction.
 *
 * `undo-opt-in` ships alongside `next-maintenance` deliberately: without the
 * inverse, a misclick would be unrecoverable from the portal. RDS only accepts
 * it while the action has not started.
 */
export const optInTypes = ['immediate', 'next-maintenance', 'undo-opt-in'] as const
export const optInTypeSchema = z.enum(optInTypes)
export type OptInType = z.infer<typeof optInTypeSchema>

export const applyMaintenanceActionRequestSchema = z.object({
  /**
   * The cluster or a member instance. Advisory only — the server re-derives the
   * set it will act on from DescribeDBClusters and rejects anything outside it,
   * because the IAM grant necessarily covers `db:*` in the account and region.
   */
  resourceIdentifier: z.string().min(1),
  /** An RDS action name, e.g. `system-update`. Must already be pending. */
  action: z.string().min(1),
  optInType: optInTypeSchema,
})
export type ApplyMaintenanceActionRequest = z.infer<typeof applyMaintenanceActionRequestSchema>

/**
 * RDS's post-apply view of that one resource, echoed back so the caller can
 * render the new opt-in state immediately. A bare `{ ok: true }` plus a refetch
 * can show a stale `optInStatus` and read as "nothing happened".
 */
export const applyMaintenanceActionResponseSchema = z.object({
  resourceIdentifier: z.string(),
  pendingActions: z.array(pendingMaintenanceActionSchema),
})
export type ApplyMaintenanceActionResponse = z.infer<typeof applyMaintenanceActionResponseSchema>

export const engineHistoryEntrySchema = z.object({
  observedAt: z.string(),
  engineVersion: z.string(),
  previousVersion: z.string().nullable(),
  source: z.enum(['baseline', 'poll']),
})
export type EngineHistoryEntry = z.infer<typeof engineHistoryEntrySchema>

export const engineEventSchema = z.object({
  date: z.string(),
  message: z.string(),
  categories: z.array(z.string()),
})
export type EngineEvent = z.infer<typeof engineEventSchema>

/**
 * Stable keys for the four optional sections of the engine response. Each is
 * backed by its own read and can fail independently of the others; a failed
 * read puts its key in `degraded` instead of failing the whole request.
 */
export const engineSections = [
  'upgradeTargets',
  'pendingActions',
  'recentEvents',
  'history',
] as const
export type EngineSection = (typeof engineSections)[number]

export const engineResponseSchema = z.object({
  clusterIdentifier: z.string(),
  engine: z.string(),
  currentVersion: z.string(),
  pinnedVersion: z.string(),
  /** True when the live cluster has moved off the version CDK pins. */
  versionDrift: z.boolean(),
  pendingVersion: z.string().nullable(),
  preferredMaintenanceWindow: z.string().nullable(),
  upgradeTargets: z.array(engineUpgradeTargetSchema),
  pendingActions: z.array(pendingMaintenanceActionSchema),
  /** Durable history, newest first. Empty until the daily recorder first runs. */
  history: z.array(engineHistoryEntrySchema),
  /** Live RDS events, newest first. RDS retains these for 14 days only. */
  recentEvents: z.array(engineEventSchema),
  /**
   * Section keys whose backing read failed. Those sections come back empty
   * rather than failing the whole request, so the core version fields survive a
   * throttled RDS describe or a DynamoDB permission error. Empty is healthy.
   * Values are `engineSections` members.
   */
  degraded: z.array(z.string()),
})
export type EngineResponse = z.infer<typeof engineResponseSchema>

/**
 * Writer availability over one trailing window, from CloudWatch's
 * `ServerlessDatabaseCapacity` sampled in fixed slots. A slot counts as awake
 * when the writer ran above zero ACUs at any point in it, so short wake-ups
 * round up to a whole slot.
 */
export const engineUptimeWindowSchema = z.object({
  hours: z.number(),
  /** awakeMinutes / sampledMinutes; null when CloudWatch returned no samples. */
  awakeRatio: z.number().nullable(),
  awakeMinutes: z.number(),
  /** Minutes CloudWatch has a sample for. Below expectedMinutes means gaps. */
  sampledMinutes: z.number(),
  expectedMinutes: z.number(),
  /** Paused → awake transitions inside the window. */
  wakeCount: z.number(),
})
export type EngineUptimeWindow = z.infer<typeof engineUptimeWindowSchema>

export const engineUptimeDaySchema = z.object({
  /** UTC calendar day, YYYY-MM-DD. */
  date: z.string(),
  awakeMinutes: z.number(),
  sampledMinutes: z.number(),
})
export type EngineUptimeDay = z.infer<typeof engineUptimeDaySchema>

export const engineUptimeResponseSchema = z.object({
  instanceIdentifier: z.string(),
  periodSeconds: z.number(),
  /** Start of the newest sampled slot. */
  latestSampleAt: z.string().nullable(),
  /** Whether the newest slot was awake; null with no samples at all. */
  currentlyAwake: z.boolean().nullable(),
  /** Start of the slot of the most recent wake-up / pause seen in the data. */
  lastWokeAt: z.string().nullable(),
  lastPausedAt: z.string().nullable(),
  /** Trailing windows, shortest first (24h, 7d, 30d). */
  windows: z.array(engineUptimeWindowSchema),
  /** One row per UTC day, oldest first, ending today. */
  daily: z.array(engineUptimeDaySchema),
})
export type EngineUptimeResponse = z.infer<typeof engineUptimeResponseSchema>

/** One Postgres log file as the RDS console's Logs tab lists it. */
export const engineLogFileSchema = z.object({
  fileName: z.string(),
  size: z.number(),
  lastWritten: z.string(),
})
export type EngineLogFile = z.infer<typeof engineLogFileSchema>

export const engineLogsResponseSchema = z.object({
  instanceIdentifier: z.string(),
  /** Newest first. */
  files: z.array(engineLogFileSchema),
  totalSize: z.number(),
  newestWrittenAt: z.string().nullable(),
})
export type EngineLogsResponse = z.infer<typeof engineLogsResponseSchema>

export const engineLogDownloadQuerySchema = z.object({
  file: z.string().min(1).max(512),
})
